import { ResultDisplayProps } from '../../common'
import React, { useMemo } from 'react'
import { AIResultStatus } from '@zocks-communications/client-sdk-js'
import { NoMeetingResult } from '../../DefaultDisplay/View/NoMeetingResult'
import { StructuredSummaryResult } from '../../../../../../../models/AiResult'
import './FutureMeetings.scss'
import { Button } from '../../../../../../../components/Button/Button'
import { formatDate } from '../../../../common/aifeatures/utils'
import { useGetMeQuery } from '../../../../../../../store/user/user'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { selectRoomSession } from '../../../../../../../store/meetingSummary/roomSession'
import { IntegrationServiceId } from '../../../../../../../common/integrations'

export const FutureMeetings: React.FC<ResultDisplayProps> = ({ aiFeature, aiResult }) => {
  if (aiResult.status === AIResultStatus.NO_RESULT) {
    return <NoMeetingResult />
  }
  const structuredSummaryResult = aiResult.result as StructuredSummaryResult

  return (
    <div>
      <div className='d-flex flex-column gap-3'>
        {Object.entries(structuredSummaryResult.result).map(([k, v]) => {
          return <FutureMeeting key={k} name={k} value={v} />
        })}
      </div>
    </div>
  )
}

interface FutureMeetingProps {
  name: string
  value: string
}

const FutureMeeting: React.FC<FutureMeetingProps> = ({ name, value }) => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)

  const { data: currentUser } = useGetMeQuery({ accountId })
  const hasGoogleCalendarIntegration = useMemo(() => {
    return !!(currentUser && currentUser.integrations && currentUser.integrations.some((i) => i.serviceId === IntegrationServiceId.GOOGLE_CALENDAR))
  }, [currentUser])
  const hasOutlookCalendarIntegration = useMemo(() => {
    return !!(currentUser && currentUser.integrations && currentUser.integrations.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR))
  }, [currentUser])
  const hasCalendarIntegration = useMemo(
    () => hasGoogleCalendarIntegration || hasOutlookCalendarIntegration,
    [hasGoogleCalendarIntegration, hasOutlookCalendarIntegration],
  )
  const ONE_HOUR_IN_MS = 60 * 60 * 1000
  const date = new Date(value)
  const formattedDate = formatDate(value)
  const getGoogleFormattedDate = (date: Date) => {
    if (isNaN(date.getTime())) {
      date = new Date()
    }
    let isoString = date.toISOString()
    let year = isoString.slice(0, 4)
    let month = isoString.slice(5, 7)
    let day = isoString.slice(8, 10)
    let hours = isoString.slice(11, 13)
    let minutes = isoString.slice(14, 16)
    let seconds = isoString.slice(17, 19)
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`
  }
  const getOutlookFormattedDate = (date: Date) => {
    if (isNaN(date.getTime())) {
      date = new Date()
    }
    return date.toISOString()
  }

  const generateCalendarDeepLink = (startDate: Date, endDate: Date, emails: string[]) => {
    if (hasGoogleCalendarIntegration) {
      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${getGoogleFormattedDate(startDate)}/${getGoogleFormattedDate(
        endDate,
      )}&add=${emails.join(',')}`
    } else {
      let url = `https://outlook.office.com/calendar/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${name}&to=${emails.join(',')}`
      if (!isNaN(startDate.getTime())) {
        url = url.concat(`&startdt=${getOutlookFormattedDate(startDate)}&enddt=${getOutlookFormattedDate(endDate)}`)
      }
      return url
    }
  }

  const handleClickOnAddEvent = () => {
    const endDate = new Date(date.getTime() + ONE_HOUR_IN_MS)
    if (hasCalendarIntegration && roomSession) {
      const emails = roomSession.users.filter((u) => u.email != null).map((u) => u.email)
      const url = generateCalendarDeepLink(date, endDate, emails)
      window.open(url, '_blank')
    }
  }

  return (
    <div className='future-meeting-container p-3'>
      <div className='d-flex justify-content-between'>
        <div>
          <div className='paragraph-large bold'>{name}</div>
          <div>{formattedDate !== 'Invalid Date' ? formattedDate : 'Date not mentioned.'}</div>
        </div>
        <div className='d-flex align-items-center'>
          <OverlayTrigger trigger={!hasCalendarIntegration ? ['hover', 'focus'] : []} overlay={<Tooltip>Connect your calendar.</Tooltip>}>
            <div>
              <Button variant='primary' className=' d-flex align-items-center' onClick={handleClickOnAddEvent} disabled={!hasCalendarIntegration}>
                +
              </Button>
            </div>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  )
}
